/**
 * Aufgaben-Seite mit Kontext (Konzept ~/maestro-uebergabe/konzept-aufgaben.html, Stufe 1+2):
 * macht aus Auto-Aufgaben und Notizen EINE Zeilenart und ordnet sie.
 *
 * Was hier NICHT passiert: kein Satz wird formuliert, kein Betrag gerechnet. Grund und Aktion
 * kommen fertig vom Server (`apps/api/src/lib/tasks-board.ts`, E5); hier wird nur gruppiert
 * (E2: Überfällig · Heute · Diese Woche · Später, innen nach Event-Datum) und sortiert.
 */
import { heuteInZone } from "@maestro/feiertage";
import { vorgangPfad } from "./pfade";
import type { AutoTask, Member, Task, TaskKontext } from "../ui/types";

export type AufgabenArt = "zahlung" | "angebot" | "anfrage" | "notiz" | "betrieb";
export type DringlichkeitsGruppe = "ueberfaellig" | "heute" | "woche" | "spaeter";

export const DRINGLICHKEIT_TITEL: Record<DringlichkeitsGruppe, string> = {
  ueberfaellig: "Überfällig",
  heute: "Heute",
  woche: "Diese Woche",
  spaeter: "Später",
};
export const DRINGLICHKEIT_REIHENFOLGE: readonly DringlichkeitsGruppe[] = ["ueberfaellig", "heute", "woche", "spaeter"];

/** Filter-Reiter: Beschriftung je Art (Reihenfolge = Anzeige). */
export const ART_TITEL: Record<AufgabenArt, string> = {
  zahlung: "Zahlungen",
  angebot: "Angebote",
  anfrage: "Anfragen",
  notiz: "Notizen",
  betrieb: "Betrieb",
};

const ART_VON_KIND: Record<AutoTask["kind"], AufgabenArt> = {
  payment_overdue: "zahlung",
  payment_dunning_exhausted: "zahlung",
  offer_awaiting_reply: "angebot",
  offer_action_needed: "angebot",
  offer_empty: "angebot",
  inquiry_stale: "anfrage",
  onboarding_gap: "betrieb",
  // KÜ V2 (E4): die geänderte Fassung hängt am Angebot, das der Gast noch nicht unterschrieben hat.
  kue_aenderung_offen: "angebot",
};

export interface AufgabenZeile {
  key: string;
  art: AufgabenArt;
  /** Firma, sonst Personenname — die Überschrift (das merkt man sich). */
  kunde: string | null;
  /** Personenname, wenn die Überschrift die Firma ist. */
  person: string | null;
  anlass: string | null;
  /** Posteingang S2: Vorgangsnummer, klein neben dem Titel — nie der Titel selbst. */
  nummer: string | null;
  gaeste: number | null;
  eventDate: string | null;
  eventStatus: string | null;
  faelligAm: string | null;
  kopf: string;
  rest: string;
  aktion: { text: string; ziel: string } | null;
  /** Stufe 3: Anzeigename der zuständigen Person (nur Notizen; Auto-Aufgaben haben keine). */
  zustaendig: string | null;
  auto?: AutoTask;
  notiz?: Task;
}

/** Heutiges Datum in Berlin, "YYYY-MM-DD" — dieselbe Zone wie `faelligAm` vom Server. */
export function heuteBerlin(jetzt: Date = new Date()): string {
  return heuteInZone("Europe/Berlin", jetzt);
}

/** Kalendertage von `von` bis `bis` ("YYYY-MM-DD"), negativ = vorbei. */
export function tageZwischen(von: string, bis: string): number {
  return Math.round((Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${von}T00:00:00Z`)) / 86_400_000);
}

/** E2: in welche Dringlichkeitsgruppe eine Fälligkeit fällt. Ohne Termin ⇒ Später. */
export function gruppeVon(faelligAm: string | null | undefined, heute: string): DringlichkeitsGruppe {
  if (!faelligAm) return "spaeter";
  const t = tageZwischen(heute, faelligAm);
  if (t < 0) return "ueberfaellig";
  if (t === 0) return "heute";
  if (t <= 6) return "woche";
  return "spaeter";
}

/** „heute“ · „morgen“ · „in 42 Tagen“ · „vor 3 Tagen“ — Abstand zum Event. */
export function tageText(t: number): string {
  if (t === 0) return "heute";
  if (t === 1) return "morgen";
  if (t === -1) return "gestern";
  return t > 1 ? `in ${t} Tagen` : `vor ${-t} Tagen`;
}

function kundeUndPerson(company: string | null | undefined, name: string | null | undefined) {
  const firma = company?.trim() || null;
  const person = name?.trim() || null;
  return { kunde: firma ?? person, person: firma ? person : null };
}

export function zeileAusAuto(a: AutoTask): AufgabenZeile {
  return {
    key: a.signature,
    art: ART_VON_KIND[a.kind] ?? "betrieb",
    ...kundeUndPerson(a.company, a.customerName),
    anlass: a.anlass ?? null,
    nummer: a.vorgangsnummer ?? null,
    gaeste: a.guests ?? null,
    eventDate: a.eventDate ?? null,
    eventStatus: a.eventStatus ?? null,
    faelligAm: a.faelligAm ?? null,
    // Ältere Antwort ohne Stufe-2-Felder: Titel/Hinweis wie bisher statt einer leeren Zeile.
    kopf: a.grund?.kopf ?? a.title,
    rest: a.grund?.rest ?? a.dueHint ?? "",
    aktion: a.aktion ?? { text: "Öffnen", ziel: a.deepLink },
    zustaendig: null,
    auto: a,
  };
}

/**
 * Stufe 3: Name statt ID-Schnipsel — Anzeigename, sonst E-Mail (dieselbe Reihenfolge wie
 * `GET /api/members`). Unbekannt (z. B. ausgetreten) ⇒ „ehemaliges Mitglied“, nie eine ID.
 */
export function mitgliedName(members: readonly Member[], stackUserId: string | null | undefined): string | null {
  if (!stackUserId) return null;
  const m = members.find((x) => x.stackUserId === stackUserId);
  return m?.displayName?.trim() || m?.email || "ehemaliges Mitglied";
}

/**
 * Filter „Nur meine“: Notizen, die mir zugewiesen sind — oder die ich ohne Zuständigen angelegt
 * habe. Auto-Aufgaben haben keine zuständige Person (sie gehören dem Betrieb) und fallen heraus.
 */
export function istMeine(z: AufgabenZeile, ichId: string | null | undefined): boolean {
  if (!ichId || !z.notiz) return false;
  return z.notiz.assignee === ichId || (!z.notiz.assignee && z.notiz.createdBy === ichId);
}

export function zeileAusNotiz(t: Task & { kontext?: TaskKontext | null }, zustaendig: string | null = null): AufgabenZeile {
  const k = t.kontext ?? null;
  return {
    key: `notiz:${t.id}`,
    art: "notiz",
    ...kundeUndPerson(k?.company, k?.customerName),
    anlass: k?.anlass ?? null,
    nummer: k?.vorgangsnummer ?? null,
    gaeste: k?.guests ?? null,
    eventDate: k?.eventDate ?? null,
    eventStatus: k?.eventStatus ?? null,
    faelligAm: t.dueDate,
    kopf: t.title,
    rest: ["Notiz", zustaendig, t.dueTime ? `${t.dueTime.slice(0, 5)} Uhr` : null].filter(Boolean).join(" · "),
    aktion: t.eventId ? { text: "Zum Vorgang", ziel: vorgangPfad(t.eventId) } : null,
    zustaendig,
    notiz: t,
  };
}

/** Nach Event-Datum aufsteigend, ohne Termin ans Ende; bei Gleichstand nach Fälligkeit. */
export function nachEventDatum(a: AufgabenZeile, b: AufgabenZeile): number {
  if (a.eventDate !== b.eventDate) {
    if (!a.eventDate) return 1;
    if (!b.eventDate) return -1;
    return a.eventDate < b.eventDate ? -1 : 1;
  }
  return (a.faelligAm ?? "9999") < (b.faelligAm ?? "9999") ? -1 : (a.faelligAm ?? "9999") > (b.faelligAm ?? "9999") ? 1 : 0;
}

export interface AufgabenAbschnitt {
  key: DringlichkeitsGruppe | "event";
  titel: string;
  zeilen: AufgabenZeile[];
}

/**
 * E2: Standard = Dringlichkeitsgruppen, innen nach Event-Datum; Umschalter „nach Event-Datum“ =
 * eine einzige Liste. Leere Gruppen fallen weg.
 */
export function ordneAufgaben(zeilen: AufgabenZeile[], heute: string, modus: "dringlichkeit" | "event"): AufgabenAbschnitt[] {
  const sortiert = [...zeilen].sort(nachEventDatum);
  if (modus === "event") return sortiert.length ? [{ key: "event", titel: "Nach Event-Datum", zeilen: sortiert }] : [];
  return DRINGLICHKEIT_REIHENFOLGE.map((g) => ({
    key: g,
    titel: DRINGLICHKEIT_TITEL[g],
    zeilen: sortiert.filter((z) => gruppeVon(z.faelligAm, heute) === g),
  })).filter((a) => a.zeilen.length > 0);
}

/**
 * E1 (Aufgaben mit Kontext): ein wartendes Angebot (`offer_awaiting_reply`, „Kunde ist dran“) ist
 * keine Aufgabe und darum auch kein Vorschlag. Vorgeschlagen wird nur `offer_action_needed` — und
 * die Kachel benennt den Handlungsbedarf selbst (Grund-Satz vom Server, z. B. „Angebot seit neun
 * Tagen abgelaufen, keine Zusage“) samt der Aktion, die der Server dafür nennt (Nachfassen bzw.
 * Als verloren schließen), statt pauschal „Nachfassen“.
 */
export function angebotsVorschlag(a: AutoTask): { titel: string; hinweis: string | null; verb: string; prompt: string } {
  const worum = [a.anlass, a.company || a.customerName].filter(Boolean).join(" · ") || a.title;
  const bedarf = a.grund?.kopf.replace(/\.$/, "") ?? a.dueHint ?? "Angebot braucht eine Entscheidung";
  const verb = a.aktion?.text ?? "Ansehen";
  return {
    titel: `${bedarf} — ${worum}`,
    hinweis: a.grund?.rest || null,
    verb,
    prompt: `Beim Angebot „${worum}“ muss ich handeln: ${bedarf}. ${verb.includes("Nachfassen") ? "Hilf mir, freundlich nachzufassen." : "Hilf mir, zu klären, ob ich nachfasse oder den Vorgang als verloren schließe."}`,
  };
}
